import React from 'react';
import { FormGroup } from 'reactstrap';

import { Form } from '@sharingapples/form';
import {
  injectStripe,
  CardCVCElement,
  CardNumberElement,
  CardExpiryElement,
} from 'react-stripe-elements';

import { api, endpoints } from '@dom/utils';

import StripeInput from './StripeInput';
import {
  InputWithError,
  TextInput,
  Validators,
  LabelledCheckInput,
  FormSubmitButton,
} from '@dom/components';

import supportedCards from './images/cards.png';
import poweredByStrip from './images/powered_by_stripe.png';

import './stripeCheckoutForm.scss';

const { required, isValidEmail, requiredCheckbox } = Validators;
const TermsOfServiceLabel = (
  <span>
    By clicking complete transaction you have our guarantee and agree with our{' '}
    <a
      href="/policies/terms-services"
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: '#3A5DB7' }}
    >
      terms and conditions.
    </a>{' '}
    Thank you for trusting our services.
  </span>
);


function StripeCheckoutForm(props) {
  const [errors, setErrors] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const promocodeRef = React.useRef(null);

  const handleErrors = (element, message) => {
    setErrors({ ...errors, [element]: message });
  };

  promocodeRef.current = props.promocode;

  const handleSubmit = async value => {
    setIsLoading(true);

    const { invoiceId, stripe, onSuccess, onError } = props;

    try {
      const { cardName, email, phoneNumber, zipCode } = value;

      const { token } = await stripe.createToken({
        name: cardName,
        email,
        address_zip: zipCode,
      });

      await api.postWithoutToken(endpoints.payment.process(invoiceId), {
        stripe_token: token.id,
        buyer_phonenumber: phoneNumber,
        ...(promocodeRef.current ? { promocode: promocodeRef.current } : {}),
      });

      onSuccess();
    } catch (err) {
      onError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const { buttonIdForTracking } = props;
  return (
    <>
      <h2>
        CREDIT CARD INFORMATION
        <span>
          <img src={supportedCards} alt="cards we accept" />
        </span>
        <span className="pr-2">
          <img src={poweredByStrip} alt="powered_by_stripe" />
        </span>
      </h2>

      <blockquote className="blockquote">
        <p className="mb-0">
          This is a secure 128-bit SSL encrypted payment via Stripe. You're
          safe.
        </p>
      </blockquote>
      <Form
        defaultValue={{ cardName: '', email: '', phoneNumber: '', zipCode: '' }}
        onSubmit={handleSubmit}
      >
        <InputWithError
          className="form-strip"
          Type={TextInput}
          inputType="name"
          name="cardName"
          label="Name on card"
          placeholder="John Doe"
          validator={required('Please inform us your name on the card')}
          autoFocus
        />

        <InputWithError
          className="form-strip"
          Type={TextInput}
          inputType="email"
          name="email"
          label="Email"
          placeholder="example@domain.com"
          small="(Trip Details will be sent to this email address)"
          validator={[
            required('Please inform us your email'),
            isValidEmail('Please inform us your valid email'),
          ]}
        />

        <InputWithError
          className="form-strip"
          Type={TextInput}
          inputType="tel"
          name="phoneNumber"
          label="Phone Number"
          placeholder="+12025550188"
          small="(Please enter your country code too for faster communication)"
          validator={required('Please inform us your phone number')}
        />

        <StripeInput
          className="form-strip"
          Type={CardNumberElement}
          name="cardNumber"
          label="Card Number"
          small="(The 16 digits on front of your card)"
          setError={handleErrors}
          error={errors['cardNumber']}
        />

        <StripeInput
          className="form-strip"
          Type={CardCVCElement}
          name="cvv"
          label="CVV Code"
          small="(The last 3 or 4 digits displayed on the back of your card)"
          setError={handleErrors}
          error={errors['cardCvc']}
        />

        <StripeInput
          className="form-strip"
          Type={CardExpiryElement}
          name="expiry"
          label="Expiry Date"
          setError={handleErrors}
          error={errors['cardExpiry']}
        />

        <InputWithError
          className="form-strip"
          Type={TextInput}
          name="zipCode"
          label="Zip Code"
          placeholder="4235"
          validator={required('Please inform us your zipCode')}
        />

        <InputWithError
          inputType="checkbox"
          className="form-strip"
          Type={LabelledCheckInput}
          name="termsAndCondition"
          label={TermsOfServiceLabel}
          validator={requiredCheckbox('Please agree to our terms of service')}
        />
        <FormGroup>
          <FormSubmitButton
            id={buttonIdForTracking}
            buttonText={
              isLoading ? 'Completing Transaction' : 'Complete Transaction'
            }
            className={isLoading ? 'btn-loading' : ''}
          />
        </FormGroup>
      </Form>
    </>
  );
}

export default injectStripe(StripeCheckoutForm);
